import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { dispatch } from '../../utils/event-helpers';
import type {
  AscAccordionPanel,
  HeadingLevel,
  PanelType,
} from '../../elements/asc-accordion-panel/asc-accordion-panel';

/**
 * Public contract for the `asc-accordion-group` host element consumed by this
 * controller. Exported so `AscAccordionGroup` can explicitly `implements` it,
 * ensuring the host always satisfies what the controller requires.
 */
export interface AccordionGroupHost extends ReactiveControllerHost, HTMLElement {
  multiple: boolean;
  panelType: PanelType;
  headingLevel: HeadingLevel;
}

/**
 * Manages accordion panel orchestration for `asc-accordion-group`:
 * - Syncs `type` and `headingLevel` to all slotted panels on demand
 * - Enforces exclusive-open mode when `multiple` is `false`
 * - Fires `asc-change` after any open-state change
 * - Handles arrow-key focus navigation between panel triggers
 *
 * Co-located with the widget because this controller is tightly coupled
 * to `asc-accordion-group` and has no reuse value outside it.
 */
export class AccordionGroupController implements ReactiveController {
  private readonly _host: AccordionGroupHost;

  constructor(host: AccordionGroupHost) {
    this._host = host;
    host.addController(this);
  }

  hostConnected(): void {
    this._host.addEventListener('asc-toggle', this._handleToggle);
    this._host.addEventListener('keydown', this._handleKeyDown);
  }

  hostDisconnected(): void {
    this._host.removeEventListener('asc-toggle', this._handleToggle);
    this._host.removeEventListener('keydown', this._handleKeyDown);
  }

  /** Returns all `asc-accordion-panel` children assigned to the default slot. */
  getPanels(): AscAccordionPanel[] {
    const slot = this._host.shadowRoot?.querySelector<HTMLSlotElement>('slot');
    if (!slot) return [];
    return slot
      .assignedElements({ flatten: true })
      .filter((el): el is AscAccordionPanel => el.tagName === 'ASC-ACCORDION-PANEL');
  }

  /** Propagates `panelType` and `headingLevel` from the host to every child panel. */
  syncPanels(): void {
    for (const panel of this.getPanels()) {
      panel.type = this._host.panelType;
      panel.headingLevel = this._host.headingLevel;
    }
  }

  private readonly _handleToggle = (e: Event): void => {
    const customEvent = e as CustomEvent<{ open: boolean }>;
    const target = e.target as AscAccordionPanel;

    // In exclusive mode, close all siblings when a panel opens.
    if (!this._host.multiple && customEvent.detail.open) {
      for (const panel of this.getPanels()) {
        if (panel !== target) panel.open = false;
      }
    }

    // Fire a single settled-state event so consumers don't need to reconcile
    // multiple asc-toggle events from cascading exclusive-mode closes.
    const openPanels = this.getPanels().filter((p) => p.open);
    dispatch<{ target: HTMLElement; open: boolean; openPanels: HTMLElement[] }>(
      this._host,
      'asc-change',
      { detail: { target, open: customEvent.detail.open, openPanels } },
    );
  };

  private readonly _handleKeyDown = (e: KeyboardEvent): void => {
    const allPanels = this.getPanels();
    if (!allPanels.length) return;

    // Only handle keydowns that originate from a panel trigger — not from
    // focusable elements inside an open panel's body (inputs, links, etc.).
    // When a keydown crosses a shadow boundary, the event is retargeted so
    // e.target is the panel host element itself.
    if (!allPanels.includes(e.target as AscAccordionPanel)) return;

    // Build the navigable set: skip disabled panels whose trigger cannot receive focus.
    const navigable = allPanels.filter((p) => !p.disabled);
    if (!navigable.length) return;

    const currentIndex = navigable.indexOf(e.target as AscAccordionPanel);

    let targetIndex = currentIndex;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      targetIndex = (currentIndex + 1) % navigable.length;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      targetIndex = (currentIndex - 1 + navigable.length) % navigable.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      targetIndex = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      targetIndex = navigable.length - 1;
    } else {
      return;
    }

    navigable[targetIndex].focusTrigger();
  };
}
